/**
 * Which payment outcomes a screen is currently on hand to announce itself.
 *
 * A settled payment is announced twice unless somebody yields. The screens that already
 * follow one payment — the payment-status page, and either wizard sitting in front of a QR
 * code — say so themselves, in their own words and at the right moment in their own flow.
 * The global listener in `usePaymentNotifications` is what covers everywhere else, and it
 * has no way of knowing whether one of those screens is open.
 *
 * So the screens register the payment they are watching, and the global listener stays quiet
 * for exactly those. Whoever is closest to the customer speaks.
 *
 * Registered PER OUTCOME, not per payment: a screen that announces settlement does not
 * necessarily announce a refusal. The storage wizard is exactly that — it reacts to
 * `PaymentVerified` and has nothing to say about `PaymentRejected` — so muting it for both
 * would leave a rejected payment announced by nobody at all. A registration means "I will
 * say this myself", and it has to be that precise or it silences the wrong thing.
 *
 * Counted rather than flagged: a payment can legitimately be watched twice at once (the
 * storage wizard mounts `usePaymentStatus` alongside its own QR poll), and the first
 * unmount must not un-register the other watcher.
 */

/** The outcomes a screen can claim. Mirrors the events the global listener handles. */
export type PaymentOutcome = "verified" | "rejected" | "refunded";

const ALL_OUTCOMES: readonly PaymentOutcome[] = [
  "verified",
  "rejected",
  "refunded",
];

const watchers: Record<PaymentOutcome, Map<string, number>> = {
  verified: new Map(),
  rejected: new Map(),

  // Nothing claims this today — no screen reacts to a refund at all, which is why the global
  // listener is the only thing that tells the customer. It is here so that the first screen to
  // grow its own refund copy has the same way out as the other two, instead of rediscovering
  // the duplicate-announcement bug.
  refunded: new Map(),
};

/**
 * Declares that a screen is showing this payment and will announce the given outcomes itself.
 * Defaults to both, which is what a screen handling the full lifecycle wants.
 * Returns the function that stands it down — call it on unmount.
 */
export function registerPaymentWatcher(
  paymentId: string,
  outcomes: readonly PaymentOutcome[] = ALL_OUTCOMES,
): () => void {
  for (const outcome of outcomes) {
    const counts = watchers[outcome];
    counts.set(paymentId, (counts.get(paymentId) ?? 0) + 1);
  }

  let released = false;
  return () => {
    // Guarded because React may invoke the same cleanup twice under StrictMode. The count is
    // clamped at zero, so the damage is not a negative number — it is that the second decrement
    // spends someone else's registration: the other screen still watching this payment is stood
    // down with it, and the global listener then speaks over a screen that is about to speak too.
    if (released) return;
    released = true;

    for (const outcome of outcomes) {
      const counts = watchers[outcome];
      const next = (counts.get(paymentId) ?? 1) - 1;
      if (next <= 0) {
        counts.delete(paymentId);
      } else {
        counts.set(paymentId, next);
      }
    }
  };
}

/** Whether a screen is on hand to announce this outcome for this payment itself. */
export function hasPaymentWatcher(
  paymentId: string,
  outcome: PaymentOutcome,
): boolean {
  return (watchers[outcome].get(paymentId) ?? 0) > 0;
}

/**
 * The toast id every "payment settled" announcement carries, so two voices on one settlement
 * update one toast instead of stacking two.
 *
 * The watcher above cannot cover one order of events: the status page's poll notices the
 * settlement first, announces it and navigates away — which releases its watcher — and the
 * socket event lands a moment later on the global listener, which now sees nobody watching.
 */
export function paymentSettledToastId(paymentId: string): string {
  return `payment-settled-${paymentId}`;
}

/** Test seam — drops every registration. */
export function resetPaymentWatchers(): void {
  for (const outcome of ALL_OUTCOMES) {
    watchers[outcome].clear();
  }
}
